<template>
  <div class="geofence-configure-screen">
    <label class="configure-item location-name">
      <input type="text"
             name="location_name"
             placeholder="Location name"
             v-validate="'required|max:64'"
             data-vv-as="location name"
             v-model="location.name"
             @input="onInput" />

      <div v-show="errors.has('location_name')"
           class="validation-error">
        {{ errors.first('location_name') }}
      </div>

      <div v-show="namesDuplicated" class="validation-error">
          {{ $t("geofence.locationWithThisNameExists") }}
      </div>
    </label>

    <div class="divider"></div>

    <div class="configure-item one-liner address">
      {{ address }}
    </div>

    <div class="divider"></div>

    <div class="configure-item">
      <div>
        {{ $t("geofence.alertOnArrived") }}
      </div>

      <base-toggle
        :value="onArrived"
        @change="onArrivedCahnge"
      />
    </div>

    <div class="divider"></div>

    <div class="configure-item">
      <div>
        {{ $t("geofence.alertOnLeft") }}
      </div>

      <base-toggle
        :value="onLeft"
        @change="onLeftCahnge"
      />
    </div>

    <div class="divider"></div>

    <div class="child-list">
      <div class="list-title">
        {{ $t("geofence.alertAbout") }}
      </div>

      <div class="list">
        <base-chip v-model="wholeFamilySelected"
                   label="My whole family"
                   class="child-name"
                   @input="wholeFamilyBtnHandler" />

        <base-chip v-for="(child, i) in childrenWithAlert"
                   class="child-name"
                   v-model="child.location_alert"
                   :label="child.name"
                   :key="i"
                   @input="trackWholeFamilyBtnState" />
      </div>
    </div>

    <div class="configure-item location-hint">
      {{ $t("alerts.locationTrackingAlert") }}
    </div>

    <div v-if="updateMode"
         class="btn delete-btn"
         @click="removeLocation">
      {{ $t("geofence.deleteLocation") }}
    </div>

    <div class="btn">
      <base-button class="save-btn"
                   color="secondary"
                   :loading="saveBtnLoading"
                   @click="saveBtnHandler">
        {{ $t("save") }}
      </base-button>
    </div>
  </div>
</template>

<script>
  import geofenceApi from '@/api/geofence'
  import errorNotification from '@/mixins/errorNotification.js'

  export default {
    mixins: [
      errorNotification
    ],

    props: {
      mode: {
        type: String,
        required: true,
      },

      namedLocationId: {
        type: Number,
        required: false,
        default: null
      },

      locationFromHistory: {
        type: Object,
        required: false,
        default: () => {}
      }
    },

    data() {
      return {
        location: {
          name: '',
          latitude: null,
          longitude: null,
          accuracy: 30,
          action: null,
          address: null,
          friendly_name: null,
          groupId: null
        },
        onArrived: false,
        onLeft: false,
        childrenWithAlert: [],
        wholeFamilySelected: false,
        saveBtnLoading: false,
        namesDuplicated: false
      }
    },

    created() {
      this.initChildren()
      this.initToggles()
      this.trackWholeFamilyBtnState()
      this.initGroupId()

      if (this.createMode) {
        this.initCreateMode()
      }

      if (this.updateMode) {
        this.initUpdateMode()
      }
    },

    methods: {
      initCreateMode() {
        this.location = _.merge(this.location, this.locationFromHistory)
      },

      initUpdateMode() {
        const namedLocation = this.getNamedLocationById(this.namedLocationId)
        this.location = _.merge(this.location, namedLocation)
      },

      initToggles() {
        if (this.updateMode && this.alertSettingsFromApi.length) {
          // The first element is selected, becouse the api does not provide
          // the ability to obtain the position of the switches
          const setting = this.alertSettingsFromApi[0]

          this.onArrived = setting.alert_entry
          this.onLeft = setting.alert_exit
        }
      },

      initChildren() {
        this.childrenWithAlert = this.children.map(child => {
          return {
            ...child,
            location_alert: this.childrenIdsFromalertSettingsFromApi.includes(child.id)
          }
        })
      },

      initGroupId() {
        this.location.groupId = this.currentUser.group_id
      },

      onArrivedCahnge(value) {
        this.onArrived = value
      },

      onLeftCahnge(value) {
        this.onLeft = value
      },

      wholeFamilyBtnHandler() {
        if (this.wholeFamilySelected) {
          this.childrenWithAlert.forEach(child => {
            child.location_alert = true
          })
        } else {
          this.childrenWithAlert.forEach(child => {
            child.location_alert = false
          })
        }
      },

      trackWholeFamilyBtnState() {
        this.wholeFamilySelected = this.allChildrenSelected
      },

      saveBtnHandler() {
        this.$validator.validateAll()
          .then(valid => {
            if (!valid) {
              return
            }

            if(this.createMode) {
              this.createLocation()
            }

            if (this.updateMode) {
              this.updateLocation()
            }
          })
      },

      createLocation() {
        this.saveBtnLoading = true

        return geofenceApi.create({
          name:      this.location.name,
          group_id:  this.location.groupId,
          latitude:  this.location.latitude,
          longitude: this.location.longitude,
          accuracy:  this.location.accuracy,
          address:   this.location.address,
          action:    'alert'
        }).then(created_location => {
          this.setAlertSettings(created_location.id).then(() => {
            this.openNamedLocationsList()
            this.saveBtnLoading = false
          })
        }).catch(error => {
          if (error.message.startsWith('duplicate')) {
            this.namesDuplicated = true
          }

          this.failedRequestNotification()
          this.saveBtnLoading = false
        })
      },

      updateLocation() {
        this.saveBtnLoading = true

        return geofenceApi.update(this.namedLocationId, this.location.groupId, {
          name:      this.location.name,
          latitude:  this.location.latitude,
          longitude: this.location.longitude,
          accuracy:  this.location.accuracy,
          action:    'alert'
        }).then(updated_location => {

          this.setAlertSettings(updated_location.id).then(() => {
            this.openNamedLocationsList()
            this.saveBtnLoading = false
          })
        }).catch(error => {
          if (error.message.startsWith('duplicate')) {
            this.namesDuplicated = true
          }

          this.failedRequestNotification()
          this.saveBtnLoading = false
        })
      },

      removeLocation() {
        geofenceApi.delete(
          this.namedLocationId,
          this.location.groupId
        ).then(() => {
          this.openNamedLocationsList()
        }).catch(() => {
          this.failedRequestNotification()
        })
      },

      setAlertSettings(location_id) {
        return geofenceApi.setAlertSettings(
          location_id,
          this.location.groupId,
          this.alertSettingsFromUI
        ).catch(() => {
          this.failedRequestNotification()
        })
      },

      openNamedLocationsList() {
        if (this.$mq === 'mobile') {
          this.$router.push({
            name: 'locations'
          })

          return
        }

        this.showModal('geofence')
      },

      onInput() {
        this.namesDuplicated = false
      }
    },

    computed: {
      ...mapGetters('geofence', {
        getNamedLocationById: 'getNamedLocationById',
        getAlertSettingsById: 'getAlertSettingsById',
        alertSettings: 'getAlertSettings'
      }),

      ...mapGetters('auth', {
        currentUser: 'currentUser'
      }),

      ...mapGetters('children', {
        children: 'get'
      }),

      allChildrenSelected() {
        return this.childrenWithAlert.every(child => child.location_alert)
      },

      updateMode() {
        return this.mode === 'update'
      },

      createMode() {
        return this.mode === 'create'
      },

      address() {
        const location = this.location

        return location.address ? location.address : `${location.latitude} ${location.longitude}`
      },

      alertSettingsFromApi() {
        return this.alertSettings.filter(setting => {
          return parseInt(setting.named_location_id) === parseInt(this.namedLocationId)
        })
      },

      alertSettingsFromUI() {
        return this.childrenWithAlert.map(child => {
          return {
            user_id: child.id,
            alert_entry: child.location_alert ? this.onArrived : child.location_alert,
            alert_exit: child.location_alert ? this.onLeft : child.location_alert
          }
        })
      },

      childrenIdsFromalertSettingsFromApi() {
        return this.alertSettingsFromApi.map(setting => setting.user_id)
      }
    },

    watch: {
      alertSettings() {
        this.initUpdateMode()
        this.initToggles()
        this.initChildren()
      }
    }
  }
</script>

<style lang="scss" scoped>
  .geofence-configure-screen {
    .configure-item {
      height: 80px;
      width: 100%;

      display: flex;
      align-items: center;
      justify-content: space-between;

      padding: 0 30px;

      &-centered {
        justify-content: center;
      }
    }

    .location-name {
      position: relative;

      input {
        width: 100%;
        font-style: normal;
        font-weight: normal;
        font-size: 16px;
        line-height: 19px;

        border: none;
      }

      .validation-error {
        position: absolute;
        bottom: 12px;

        color: #ff0000;
        font-size: 12px;
      }
    }

    .child-list {
      height: auto;
      width: 100%;

      display: flex;
      flex-direction: column;

      padding: 0 30px;

      .list-title {
        margin: 30px 0 24px 0;
      }

      .list {
        display: flex;
        align-items: center;
        flex-wrap: wrap;

        .child-name {
          margin: 0 12px 24px 0;
        }
      }
    }

    .address {
      display: inline-block;
      padding-top: 35px;
    }

    .location-hint {
      color: #B9B9B9;
      font-style: normal;
      font-weight: normal;
      font-size: 12px;
      line-height: 14px;
    }

    .btn {
      height: auto;
      width: 100%;

      display: flex;
      align-items: center;
      justify-content: center;

      cursor: pointer;
      margin-bottom: 40px;
      user-select: none;

      .save-btn {
        width: 60%;
      }
    }

    .delete-btn {
      color: #FB5E55;
      font-style: normal;
      font-weight: normal;
      font-size: 16px;
      line-height: 19px;
    }

    .divider {
      height: 1px;
      width: 100%;
      background:#f2f2f2;
    }
  }
</style>